import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../data/supabaseClient';
import { useToast } from '../hooks/ToastContext';
import Modal from './Modal';
import Spinner from './Spinner';
import { Button } from '../Templates';
import './ReportUserModal.css';

/**
 * Modal for reporting users with predefined categories and custom description
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Function to close modal
 * @param {string} props.userId - ID of user being reported
 * @param {string} props.userName - Display name of user being reported
 * @param {Object} props.context - Context object with post_id, package_id, conversation_id
 * @param {Function} props.onReportSubmitted - Callback when report is successfully submitted
 */
const ReportUserModal = ({
  isOpen,
  onClose,
  userId,
  userName,
  context = {},
  onReportSubmitted
}) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [description, setDescription] = useState('');

  // Report categories with descriptions
  const reportReasons = [
    {
      value: 'spam',
      label: t('reports.reasons.spam', 'Spam'),
      description: t('reports.descriptions.spam', 'User is sending unwanted or repetitive messages')
    },
    {
      value: 'fake_profile',
      label: t('reports.reasons.fake_profile', 'Fake Profile'),
      description: t('reports.descriptions.fake_profile', 'Profile appears to be fake or impersonating someone')
    },
    {
      value: 'inappropriate_behavior',
      label: t('reports.reasons.inappropriate_behavior', 'Inappropriate Behavior'),
      description: t('reports.descriptions.inappropriate_behavior', 'User is behaving inappropriately or unprofessionally')
    },
    {
      value: 'harassment',
      label: t('reports.reasons.harassment', 'Harassment'),
      description: t('reports.descriptions.harassment', 'User is harassing or bullying others')
    },
    {
      value: 'scam_attempt',
      label: t('reports.reasons.scam_attempt', 'Scam Attempt'),
      description: t('reports.descriptions.scam_attempt', 'User appears to be attempting to scam or defraud others')
    },
    {
      value: 'no_show',
      label: t('reports.reasons.no_show', 'No Show'),
      description: t('reports.descriptions.no_show', 'User did not show up for arranged ride or delivery')
    },
    {
      value: 'dangerous_driving',
      label: t('reports.reasons.dangerous_driving', 'Dangerous Driving'),
      description: t('reports.descriptions.dangerous_driving', 'User drives dangerously or recklessly')
    },
    {
      value: 'other',
      label: t('reports.reasons.other', 'Other'),
      description: t('reports.descriptions.other', 'Other reason not listed above')
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedReason) {
      addToast({
        type: 'error',
        title: t('errors.validation_error', 'Validation Error'),
        description: t('reports.errors.select_reason', 'Please select a reason for reporting')
      });
      return;
    }

    if (selectedReason === 'other' && !description.trim()) {
      addToast({
        type: 'error',
        title: t('errors.validation_error', 'Validation Error'),
        description: t('reports.errors.description_required', 'Description is required when selecting "Other"')
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.rpc('create_user_report', {
        p_reported_user_id: userId,
        p_report_type: selectedReason,
        p_description: description.trim() || null,
        p_related_post_id: context.post_id || null,
        p_related_package_id: context.package_id || null,
        p_related_conversation_id: context.conversation_id || null
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to submit report');
      }

      addToast({
        type: 'success',
        title: t('reports.success.title', 'Report Submitted'),
        description: t('reports.success.description', 'Thank you for reporting. We will review this matter.')
      });

      // Reset form
      setSelectedReason('');
      setDescription('');
      
      // Call success callback
      if (onReportSubmitted) {
        onReportSubmitted(data);
      }
      
      // Close modal
      onClose();

    } catch (err) {
      console.error('Report submission error:', err);
      addToast({
        type: 'error',
        title: t('errors.report_failed', 'Report Failed'),
        description: err.message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedReason('');
      setDescription('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="report-user-modal">
      <div className="report-modal-header">
        <h2>{t('reports.title', 'Report User')}</h2>
        <p className="report-modal-subtitle">
          {t('reports.subtitle', 'Report {{userName}} for inappropriate behavior', { userName })}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="report-modal-form">
        <div className="report-form-section">
          <label className="report-form-label">
            {t('reports.reason_label', 'Reason for reporting')} <span className="report-required">*</span>
          </label>
          
          <div className="report-reason-options">
            {reportReasons.map((reason) => (
              <label
                key={reason.value}
                className={`report-reason-option ${selectedReason === reason.value ? 'report-selected' : ''}`}
              >
                <input
                  type="radio"
                  name="reportReason"
                  value={reason.value}
                  checked={selectedReason === reason.value}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  disabled={isSubmitting}
                />
                <div className="report-reason-content">
                  <div className="report-reason-title">{reason.label}</div>
                  <div className="report-reason-description">{reason.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="report-form-section">
          <label htmlFor="report-description" className="report-form-label">
            {t('reports.description_label', 'Additional details')}
            {selectedReason === 'other' && <span className="report-required">*</span>}
          </label>
          <textarea
            id="report-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('reports.description_placeholder', 'Provide additional details about the issue...')}
            className="report-description-textarea"
            rows={4}
            maxLength={500}
            disabled={isSubmitting}
          />
          <div className="report-character-count">
            {description.length}/500
          </div>
        </div>

        <div className="report-form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('buttons.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || !selectedReason}
            loading={isSubmitting}
          >
            {t('buttons.submit_report', 'Submit Report')}
          </Button>
        </div>
      </form>

      {isSubmitting && (
        <div className="report-loading-overlay">
          <Spinner size="medium" showText={false} />
        </div>
      )}
    </Modal>
  );
};

export default ReportUserModal;